import React, { useState, useCallback } from 'react'
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Autocomplete,
	TextField,
	CircularProgress,
	Typography,
	Box,
} from '@mui/material'
import { SmallWorker } from '../../types/Employee/Employee'
import { ScaffoldRequestForm } from '../../types/Scaffold/ScaffoldRequest'
import {
	assignScaffoldRequestToWorker,
	searchAssignees
} from '../../services/scaffoldRequestService'
import { useDebounced } from '../hooks/useDebounced'

interface AssignToDialogProps {
	open: boolean
	onClose: () => void
	scaffoldRequest: ScaffoldRequestForm
	refreshScaffolds: () => void
}

export default function AssignToDialog({ open, onClose, scaffoldRequest, refreshScaffolds }: AssignToDialogProps) {
	const [selectedEmployee, setSelectedEmployee] = useState<SmallWorker | null>(null)
	const [employeeOptions, setEmployeeOptions] = useState<SmallWorker[]>([])
	const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false)
	const [assigning, setAssigning] = useState(false)

	// Search function
	const lookupAssignees = useCallback(async (searchTerm: string) => {
		if (searchTerm.length < 2) {
			setEmployeeOptions([])
			return
		}
		setEmployeeSearchLoading(true)
		try {
			const result = await searchAssignees(searchTerm)
			setEmployeeOptions(result || [])
		} catch (error) {
			console.error('Error searching employees:', error)
			setEmployeeOptions([])
		} finally {
			setEmployeeSearchLoading(false)
		}
	}, [])

	// Debounced search function
	const debouncedSearch = useDebounced(lookupAssignees, 300)

	// Employee search function
	const searchEmployees = (searchTerm: string) => {
		debouncedSearch(searchTerm)
	}

	const handleAssign = async () => {
		if (!selectedEmployee || !scaffoldRequest) return

		setAssigning(true)
		try {
			await assignScaffoldRequestToWorker(scaffoldRequest.id!, selectedEmployee.id)
			refreshScaffolds()
			handleClose()
		} catch (error) {
			console.error('Error assigning scaffold request:', error)
		} finally {
			setAssigning(false)
		}
	}

	const handleClose = () => {
		setSelectedEmployee(null)
		setEmployeeOptions([])
		// Cancel any pending debounced search
		debouncedSearch.cancel?.()
		onClose()
	}

	return (
		<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
			<DialogTitle>Assign Scaffold Request for Estimation</DialogTitle>
			<DialogContent>
				<Box sx={{ mt: 2 }}>
					{scaffoldRequest && (
						<Box sx={{ mb: 3 }}>
							<Typography variant='subtitle1' gutterBottom>
								Scaffold Request Details:
							</Typography>
							<Typography variant='body2' color='text.secondary'>
								<strong>ID:</strong> {scaffoldRequest.id}
							</Typography>
							<Typography variant='body2' color='text.secondary'>
								<strong>Tag Number:</strong> {scaffoldRequest.tagNumber}
							</Typography>
							<Typography variant='body2' color='text.secondary'>
								<strong>Action:</strong> {scaffoldRequest.action}
							</Typography>
							<Typography variant='body2' color='text.secondary'>
								<strong>Area:</strong> {scaffoldRequest.area}
							</Typography>
						</Box>
					)}

					<Typography variant='body1' gutterBottom>
						Select a worker to assign this scaffold request for estimation:
					</Typography>

					<Box sx={{ mt: 2 }}>
						<Autocomplete
							fullWidth
							options={employeeOptions}
							getOptionLabel={(employee) => `${employee.fullName} ${employee.roleTitle ? `- ${employee.roleTitle}` : ''}`}
							value={selectedEmployee}
							onChange={(event, newValue) => setSelectedEmployee(newValue)}
							onInputChange={(event, newInputValue) => {
								searchEmployees(newInputValue)
							}}
							loading={employeeSearchLoading}
							renderInput={(params) => (
								<TextField
									{...params}
									label='Search for worker'
									placeholder='Type worker name...'
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{employeeSearchLoading ? (
													<CircularProgress color='inherit' size={20} sx={{ m: 0, p: 0 }} />
												) : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
							renderOption={(props, employee) => (
								<li {...props} key={employee.id}>
									<Box>
										<Typography variant='body1'>
											{employee.fullName}
										</Typography>
										{employee.roleTitle && (
											<Typography variant='body2' color='text.secondary'>
												{employee.roleTitle}
											</Typography>
										)}
									</Box>
								</li>
							)}
							noOptionsText='No workers found'
							loadingText='Searching workers...'
						/>
					</Box>
				</Box>
			</DialogContent>
			<DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
				<Button color='warning' variant='contained' onClick={handleClose}>
					Cancel
				</Button>
				<Button
					variant='contained'
					color='primary'
					onClick={handleAssign}
					disabled={!selectedEmployee || assigning}
					startIcon={assigning ? <CircularProgress size={20} sx={{ m: 0, p: 0 }} /> : null}
				>
					{assigning ? 'Assigning...' : 'Assign for Estimation'}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
